import { useCallback, useEffect, useRef, useState } from 'react'
import { useApi } from './useApi'
import { useWebSocket } from './useWebSocket'
import { useCallStore } from '../stores/callStore'
import { usePresenceStore, type Operator } from '../stores/presenceStore'
import { useAuthStore } from '../stores/authStore'
import { logger } from '../utils/logger'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
const PING_INTERVAL_MS = 30000
// タイマー発火間隔がこれを超えて飛んだらスリープ復帰とみなし即 ping
const SLEEP_GAP_MS = 90000

interface PresenceUser {
  organizationId?: string
}

/** サーバへ送る生シグナル (導出はサーバ側 presenceState.ts が行う) */
interface PresenceSignals {
  deviceState: 'registered' | 'unregistered' | 'error'
  micPermission: 'granted' | 'denied' | 'prompt'
  onCall: boolean
  ringMuted: boolean
}

/** Twilio Device の内部状態 → presence シグナルへの写像 (この一箇所のみ) */
function toDeviceSignal(deviceStatus: string): PresenceSignals['deviceState'] {
  if (deviceStatus === 'ready') return 'registered'
  if (deviceStatus === 'error') return 'error'
  return 'unregistered' // 'initializing' / 'offline'
}

/**
 * バックグラウンドタブの setInterval 間引き (≥1分) を避けるため、
 * タイマーは Web Worker で駆動する。生成に失敗した環境では null を返し、
 * 呼び出し側が setInterval にフォールバックする。
 */
function createIntervalWorker(intervalMs: number): Worker | null {
  try {
    const source = `setInterval(function () { postMessage(0) }, ${intervalMs});`
    const url = URL.createObjectURL(new Blob([source], { type: 'application/javascript' }))
    const worker = new Worker(url)
    URL.revokeObjectURL(url)
    return worker
  } catch (err) {
    logger.warn('[presence] タイマー Worker を生成できません。setInterval にフォールバック:', err)
    return null
  }
}

/**
 * オペレーターのPresence（在席状態）を管理するカスタムフック
 * - オンライン登録と 30 秒ごとの ping (Web Worker 駆動)
 * - 受話可能性シグナル (Device 登録・マイク権限・通話中・ミュート) の送信
 * - シグナル変化時の即時反映、スリープ復帰検知
 * - タブクローズ時の keepalive オフライン通知
 * - 初期スナップショット取得と WebSocket 再接続時の再同期
 */
export function usePresenceManager(user: PresenceUser | null, ringAudioEnabled: boolean): void {
  const api = useApi()
  // useApi は navigate に依存しており画面遷移のたびに identity が変わる。
  // それを effect の依存に入れると遷移ごとに presence effect が
  // 破棄→再実行され offline/online が競合するため、ref 経由で参照し
  // コールバックは安定させる
  const apiRef = useRef(api)
  apiRef.current = api
  const setActiveOperators = usePresenceStore((state) => state.setActiveOperators)
  const deviceStatus = useCallStore((state) => state.deviceStatus)
  const callStatus = useCallStore((state) => state.callStatus)
  const [micPermission, setMicPermission] = useState<PresenceSignals['micPermission']>('prompt')

  // interval/イベントハンドラから常に最新シグナルを送るための ref
  const signalsRef = useRef<PresenceSignals>({
    deviceState: 'unregistered',
    micPermission: 'prompt',
    onCall: false,
    ringMuted: false,
  })
  signalsRef.current = {
    deviceState: toDeviceSignal(deviceStatus),
    micPermission,
    onCall: callStatus === 'connected' || callStatus === 'connecting',
    ringMuted: !ringAudioEnabled,
  }

  // マイク権限の監視 (Permissions API 未対応環境では 'prompt' のまま =
  // サーバ側は impaired 扱いにしない)
  useEffect(() => {
    let permissionStatus: PermissionStatus | null = null
    let disposed = false
    const query = async () => {
      try {
        permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName })
        if (disposed) return
        setMicPermission(permissionStatus.state as PresenceSignals['micPermission'])
        permissionStatus.onchange = () => {
          if (permissionStatus) setMicPermission(permissionStatus.state as PresenceSignals['micPermission'])
        }
      } catch {
        // Permissions API 未対応 (一部 Safari 等) — 'prompt' のまま運用
      }
    }
    query()
    return () => {
      disposed = true
      if (permissionStatus) permissionStatus.onchange = null
    }
  }, [])

  const fetchSnapshot = useCallback(async () => {
    try {
      const data = await apiRef.current.get('/api/presence') as { items?: Operator[] }
      setActiveOperators(data.items || [])
    } catch (err) {
      logger.warn('[presence] スナップショット取得エラー:', (err as Error).message)
    }
  }, [setActiveOperators])

  const ping = useCallback(async () => {
    try {
      await apiRef.current.post('/api/presence/ping', { available: !signalsRef.current.ringMuted, signals: signalsRef.current })
    } catch {
      // ログは抑制
    }
  }, [])

  // WebSocket 再接続時: presence を再同期 (取りこぼした presence.update の回復)
  const onReconnect = useCallback(() => {
    fetchSnapshot()
    ping()
  }, [fetchSnapshot, ping])
  useWebSocket(undefined, onReconnect)

  // オンライン状態の管理とping
  useEffect(() => {
    if (!user) return
    let cancelled = false
    let worker: Worker | null = null
    let intervalId: ReturnType<typeof setInterval> | undefined
    let lastTickAt = Date.now()

    const goOnline = async () => {
      try {
        await apiRef.current.post('/api/presence/online', { available: !signalsRef.current.ringMuted, signals: signalsRef.current })
      } catch (err) {
        console.warn('presence online エラー:', (err as Error).message)
      }
    }

    const tick = () => {
      if (cancelled) return
      const now = Date.now()
      if (now - lastTickAt > SLEEP_GAP_MS) {
        logger.info('[presence] スリープ復帰を検知。即時 ping します')
      }
      lastTickAt = now
      ping()
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        ping()
      }
    }

    // タブクローズ/離脱時に即オフライン反映する (TTL 待ちのズレをなくす)。
    // keepalive fetch はアンロード後も送信が継続される
    const handlePagehide = () => {
      const token = useAuthStore.getState().getIdToken()
      if (!token) return
      try {
        fetch(`${API_BASE_URL}/api/presence/offline`, {
          method: 'POST',
          keepalive: true,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: '{}',
        }).catch(() => {})
      } catch {
        // no-op
      }
    }

    goOnline().then(() => {
      if (cancelled) return
      worker = createIntervalWorker(PING_INTERVAL_MS)
      if (worker) {
        worker.onmessage = tick
      } else {
        intervalId = setInterval(tick, PING_INTERVAL_MS)
      }
    })
    fetchSnapshot()

    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('pagehide', handlePagehide)

    return () => {
      cancelled = true
      if (worker) worker.terminate()
      if (intervalId) clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('pagehide', handlePagehide)
      // ここで offline は送らない: SPA 内の再マウントで online と競合し
      // 「一瞬オフライン」の原因になるため。明示ログアウトは authStore が、
      // タブクローズは pagehide が、異常終了は TTL が offline を担う
    }
  }, [user, ping, fetchSnapshot])

  // 受話可能性シグナルの変化を即時反映 (30 秒周期を待たない)。
  // 初回レンダーは goOnline が現在のシグナルを送るためスキップする
  const signalsSentOnceRef = useRef(false)
  useEffect(() => {
    if (!user) return
    if (!signalsSentOnceRef.current) {
      signalsSentOnceRef.current = true
      return
    }
    ping()
  }, [user, ping, deviceStatus, callStatus, ringAudioEnabled, micPermission])
}
